import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import BackButton from '@/pages/components/BackButton';
import { useBrandStore } from '@/store';

import LanguageSwitcher from './LanguageSwitcher';

const HeroBanner: React.FC = () => {
  const { t } = useTranslation();
  const { brand, getBrandName } = useBrandStore();
  const location = useLocation();
  const navigate = useNavigate();
  const isSelectDateTimeStep =
    location.pathname === '/booking' ||
    location.pathname.startsWith('/booking/select-date-time');

  // Determine back target for each booking step
  const { showBack, previousPath } = useMemo(() => {
    const path = location.pathname;
    if (path === '/booking' || path.startsWith('/booking/select-date-time')) {
      return { showBack: true, previousPath: '/profile' };
    }
    if (path.startsWith('/booking/summary')) {
      return { showBack: true, previousPath: '/booking/select-treatment' };
    }
    if (path.startsWith('/booking/select-treatment')) {
      return { showBack: true, previousPath: '/booking/select-date-time' };
    }
    return { showBack: false, previousPath: '' };
  }, [location.pathname]);

  const handleBack = () => {
    if (previousPath) {
      void navigate(previousPath);
    }
  };

  return (
    <div>
      {/* Banner section */}
      <div
        className="relative h-[166px] bg-gradient-to-r from-[#88478a] to-[#522b53]"
        style={{
          backgroundImage: brand?.bannerImageUrl
            ? `url(${brand.bannerImageUrl})`
            : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Hero background overlay */}
        <div className="absolute inset-0 bg-black opacity-20"></div>
      </div>

      {/* Profile section - positioned to overlap the banner */}
      <div className="justify-between1 relative z-10 container mx-auto -mt-[45px] flex">
        {/* Back button & (optional) title row positioned below banner (Figma Header frame at y≈174) */}
        <div className="flex w-1/3 items-end pb-8 pl-5">
          {showBack && (
            <BackButton
              onClick={handleBack}
              label={
                isSelectDateTimeStep
                  ? t('components.heroBanner.back')
                  : undefined
              }
            />
          )}
        </div>

        {/* Shop logo circle - centered and overlapping */}
        <div className="flex w-1/3 flex-col items-center justify-center">
          <div className="flex h-[90px] w-[90px] flex-col items-center justify-center overflow-hidden rounded-full bg-white shadow-xs">
            {brand?.logoImageUrl ? (
              <img
                src={brand.logoImageUrl}
                alt="Shop logo"
                className="h-full w-full rounded-full object-cover"
              />
            ) : (
              <span className="text-brand text-2xl font-bold">TH</span>
            )}
          </div>
          {/* Shop name */}
          <h1 className="text-primary-text mt-2 text-center leading-7 font-semibold">
            {getBrandName() || t('hero.shopName')}
          </h1>
        </div>

        {/* Language switcher - positioned in top right */}
        <div className="flex w-1/3 flex-col items-end justify-end pr-5 pb-6">
          <LanguageSwitcher />
        </div>
      </div>
    </div>
  );
};

export default HeroBanner;
